import { useCallback } from 'react'

import {
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query'

import {
  ChatList,
  QueryChatsByCursorArgs,
  type QueryChatsArgs,
} from '@tribeplatform/gql-client/types'

import { useTribeClient } from '../../useTribeClient.js'
import { getChatsKey } from '../../utils/keys/messaging.key.js'

import { useUnreadMessagesCount } from './useUnreadMessagesCount.js'

const isCursorQuery = (
  params: QueryChatsByCursorArgs | QueryChatsArgs,
): params is QueryChatsByCursorArgs => {
  return params && 'cursor' in params
}
export const useChats = (options: {
  variables: QueryChatsArgs
  useInfiniteQueryOptions?: UseInfiniteQueryOptions<ChatList>
}) => {
  const { variables, useInfiniteQueryOptions } = options
  const { maybeFetchUnreadMessagesCount } = useUnreadMessagesCount()

  const { client } = useTribeClient()
  const chatsKey = getChatsKey({ variables })

  const fetchChats = useCallback(
    async (params?: QueryChatsByCursorArgs | QueryChatsArgs) => {
      if (isCursorQuery(params)) {
        return client.messaging.chatsByCursor(params)
      }
      return client.messaging.chats({
        ...variables,
      })
    },
    [client.messaging, variables],
  )

  return useInfiniteQuery(
    chatsKey,
    async ({ pageParam }: { pageParam?: QueryChatsByCursorArgs }) => {
      const result = await fetchChats(pageParam)
      return result
    },
    {
      ...(useInfiniteQueryOptions ?? {}),
      getNextPageParam: (
        lastPage,
        _pages,
      ): QueryChatsByCursorArgs | undefined => {
        if (lastPage?.pageInfo.endCursor) {
          return {
            cursor: lastPage.pageInfo.endCursor,
            limit: variables.limit,
          }
        }

        return undefined
      },
      getPreviousPageParam: (
        firstPage,
        _pages,
      ): QueryChatsByCursorArgs | undefined => {
        if (firstPage?.pageInfo.startCursor) {
          return {
            cursor: firstPage.pageInfo.startCursor,
            limit: variables.limit,
          }
        }

        return undefined
      },
      onSuccess: async data => {
        try {
          const chatIds = data.pages.at(-1)?.data.map(chat => {
            return chat._id
          })
          await maybeFetchUnreadMessagesCount(chatIds, variables.archived)
        } catch (e) {
          // No - op
        }

        if (useInfiniteQueryOptions?.onSuccess) {
          useInfiniteQueryOptions.onSuccess(data)
        }
        return data
      },
    },
  )
}
